import React from 'react';
import { Paper, Title, TextInput, Select, Text, Alert, Stack, Divider, Radio, Group, Button, Badge, Collapse, UnstyledButton } from '@mantine/core';
import { IconChevronDown, IconMusic } from '@tabler/icons-react';
import MusicUpload from './MusicUpload/MusicUpload';
import { PERFORMANCE_CATEGORIES, FOOTWEAR_OPTIONS } from '../config/categories';

/**
 * One solo entry in Step 3 (variationNumber 1 or 2 — the slot it occupies).
 * Category and footwear are required to save; the piece details and music can
 * be filled in later, until registration closes.
 *
 * Props:
 *   variationNumber:   1 | 2                — which slot this represents
 *   title:             string               — heading shown at the top of the card
 *   data:              object               — the variation state object
 *   onChange:          (field, value) => void
 *   errors:            object               — validation errors keyed by "{prefix}_{field}"
 *   errorPrefix:       string               — "primary" | "secondary" (used to look up error keys)
 *   isAdult:           boolean              — controls the recreational/competitive selector
 *   ageDivision:       string               — shown under the category (from the date of birth)
 *   registrationId:    string               — needed for music upload
 *   onMusicUpload:     (musicData) => void  — invoked after upload completes
 *   onRemove:          () => void | null    — if provided, renders a "Remove" link at top-right
 *   paid:              boolean              — shows a Paid / Not paid yet badge
 *   footwearWarning:   string | null        — the 12+ soft-shoe warning, shown under footwear
 *   expanded:          boolean              — full form (true) or one-line summary (false)
 *   onToggle:          () => void           — tap the header to expand / collapse
 */
const VariationCard = ({
  variationNumber,
  title,
  data,
  onChange,
  errors,
  errorPrefix,
  isAdult,
  ageDivision,
  registrationId,
  onMusicUpload,
  onRemove,
  paid = false,
  footwearWarning = null,
  expanded = true,
  onToggle
}) => {
  const inputStyles = {
    label: { textAlign: 'left' },
    description: { textAlign: 'left' }
  };

  const performanceCategories = PERFORMANCE_CATEGORIES;

  const variationLengths = [
    { value: '1 minute', label: '1 minute' },
    { value: '1.5 minutes', label: '1.5 minutes' },
    { value: '2 minutes', label: '2 minutes' },
    { value: '2.5 minutes', label: '2.5 minutes' },
    { value: '3 minutes', label: '3 minutes' }
  ];

  const errKey = (field) => `${errorPrefix}_${field}`;

  return (
    <Paper
      radius="md"
      p={{ base: 'sm', sm: 'lg' }}
      withBorder
      style={{
        borderTop: '4px solid var(--primary-gold)',
        marginBottom: '1rem',
        position: 'relative'
      }}
    >
      <UnstyledButton
        onClick={onToggle}
        aria-expanded={expanded}
        style={{ display: 'block', width: '100%', textAlign: 'left' }}
      >
        <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
          <div style={{ minWidth: 0 }}>
            <Group gap="sm" wrap="wrap" mb={4}>
              <Title order={2} size="h4" style={{ margin: 0, color: 'var(--charcoal)' }}>
                {title}
              </Title>
              <Badge color={paid ? 'green' : 'gray'} variant="light">{paid ? 'Paid' : 'Not paid yet'}</Badge>
            </Group>
            <Text size="sm" weight={600} style={{ color: 'var(--charcoal)', overflowWrap: 'anywhere' }}>
              {data.variation_name
                ? `${data.variation_name}${data.ballet_origin ? ` — ${data.ballet_origin}` : ''}`
                : 'Piece not chosen yet'}
            </Text>
            <Text size="xs" color="dimmed">
              {[
                data.performance_category ? data.performance_category.replace('Classical Variation - ', '') : null,
                FOOTWEAR_OPTIONS.find(o => o.value === data.footwear)?.label || 'Footwear not chosen',
              ].filter(Boolean).join(' · ')}
              {' · '}
              <IconMusic size={12} style={{ verticalAlign: 'middle' }} /> {data.music_file_url ? 'Music uploaded' : 'No music yet'}
            </Text>
          </div>
          <IconChevronDown
            size={20}
            style={{
              flexShrink: 0,
              marginTop: 4,
              color: 'var(--deep-gold)',
              transform: expanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 150ms ease'
            }}
          />
        </Group>
      </UnstyledButton>

      <Collapse in={expanded}>
      {onRemove && (
        <Group justify="flex-end" mt="xs">
          <Button variant="subtle" color="red" size="xs" onClick={onRemove}>
            × Remove this solo
          </Button>
        </Group>
      )}
      <Divider my="md" />
      <Stack spacing="md">
        <Select
          label="Performance Category"
          placeholder="Select category"
          description={[
            ageDivision && `Your age division: ${ageDivision} (from your date of birth).`,
            !isAdult && 'Recreational is for adults (18+); younger dancers compete in Competitive.',
          ].filter(Boolean).join(' ') || undefined}
          required
          value={data.performance_category}
          onChange={(value) => onChange('performance_category', value)}
          data={performanceCategories}
          error={errors[errKey('performance_category')]}
          disabled={!isAdult}
          styles={inputStyles}
        />

        <div>
          <Radio.Group
            label="Footwear"
            description={isAdult && data.performance_category?.includes('Recreational')
              ? 'Adult Recreational is judged separately for pointe and soft shoes. You can change this until registration closes.'
              : 'You can change this until registration closes.'}
            required
            value={data.footwear || ''}
            onChange={(value) => onChange('footwear', value)}
            error={errors[errKey('footwear')]}
            styles={inputStyles}
          >
            <Group mt="xs" gap="xl">
              {FOOTWEAR_OPTIONS.map(o => <Radio key={o.value} value={o.value} label={o.label} />)}
            </Group>
          </Radio.Group>
          {footwearWarning && (
            <Alert color="yellow" variant="light" mt="sm" p="xs">
              <Text size="sm" style={{ textAlign: 'left' }}>{footwearWarning}</Text>
            </Alert>
          )}
        </div>

        <Text size="xs" color="dimmed" style={{ textAlign: 'left' }}>
          Not sure of your piece yet? You can fill in the details below and upload music later, until registration closes.
        </Text>

        <TextInput
          label="Variation Name"
          placeholder="e.g., Kitri Variation"
          value={data.variation_name}
          onChange={(e) => onChange('variation_name', e.target.value)}
          error={errors[errKey('variation_name')]}
          styles={inputStyles}
        />

        <TextInput
          label="Ballet of Origin"
          placeholder="e.g., Don Quixote"
          value={data.ballet_origin}
          onChange={(e) => onChange('ballet_origin', e.target.value)}
          error={errors[errKey('ballet_origin')]}
          styles={inputStyles}
        />

        <TextInput
          label="Character Name"
          placeholder="e.g., Kitri"
          value={data.character_name}
          onChange={(e) => onChange('character_name', e.target.value)}
          styles={inputStyles}
        />

        <Select
          label="Variation Length"
          placeholder="Select length"
          value={data.variation_length}
          onChange={(value) => onChange('variation_length', value)}
          data={variationLengths}
          error={errors[errKey('variation_length')]}
          styles={inputStyles}
        />
      </Stack>

      <Divider my="xl" label="Entry Information" labelPosition="center" styles={{ label: { fontWeight: 600, color: 'var(--charcoal)' } }} />

      <Radio.Group
        label="Start Music"
        description="Will the dancer be ON stage or OFF stage when the music begins?"
        value={data.start_music}
        onChange={(value) => onChange('start_music', value)}
        error={errors[errKey('start_music')]}
        styles={inputStyles}
      >
        <Group mt="xs" gap="xl">
          <Radio value="on_stage" label="ON Stage" />
          <Radio value="off_stage" label="OFF Stage" />
        </Group>
      </Radio.Group>

      <Divider my="xl" label="Music File" labelPosition="center" styles={{ label: { fontWeight: 600, color: 'var(--charcoal)' } }} />

      <div>
        <Text size="sm" weight={600} mb="xs" style={{ textAlign: 'left', color: 'var(--charcoal)' }}>
          Upload Variation Music
        </Text>
        <Text size="xs" color="dimmed" mb="md" style={{ textAlign: 'left' }}>
          Upload your music file for this variation. Must be under 3 minutes. Uploading a new file will replace any previously uploaded music.
        </Text>

        {registrationId ? (
          <MusicUpload
            registrationId={registrationId}
            variationNumber={variationNumber}
            existingMusicUrl={data.music_file_url}
            existingMusicFileName={data.music_file_name}
            onUploadComplete={onMusicUpload}
            onUploadError={(error) => console.error('Music upload error:', error)}
          />
        ) : (
          <Alert color="blue">
            <Text size="sm" style={{ textAlign: 'left' }}>
              Please save your registration first, then you can upload your music file.
            </Text>
          </Alert>
        )}
      </div>
      </Collapse>
    </Paper>
  );
};

export default VariationCard;
